import { Button } from "@/components/ui/button";
import { CardTitle } from "@/components/ui/card";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Info } from "lucide-react";
import type { ReactNode } from "react";

export function TileInfo({
  label,
  title,
  body,
  className,
}: {
  label: string;
  title: string;
  body: string;
  className?: string;
}) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn("h-5 w-5 shrink-0 text-muted-foreground", className)}
          aria-label={`${label} information`}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
        >
          <Info className="h-3 w-3" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-56 p-2 text-xs"
        onClick={(event) => event.stopPropagation()}
      >
        <p className="font-medium">{title}</p>
        <p className="mt-1 text-muted-foreground">{body}</p>
      </PopoverContent>
    </Popover>
  );
}

const DETAILS_TILE_INFO = {
  Property: {
    title: "Property",
    body: "Address, jurisdiction, and parcel for this registration.",
  },
  Requester: {
    title: "Requester",
    body: "The person or company that submitted this title request.",
  },
  Request: {
    title: "Request",
    body: "The address and APN submitted with this request, and its status.",
  },
  Correspondence: {
    title: "Correspondence",
    body: "Emails sent to the requester about this title request.",
  },
  Actions: {
    title: "Actions",
    body: "Pay, renew, cancel, de-register, approve, or reject when that action is available.",
  },
  "Issues & Comments": {
    title: "Issues & Comments",
    body: "Notes on this registration, including compliance issues and whether the registrant has reported one resolved.",
  },
  License: {
    title: "License",
    body: "Status, dates, and registration details for this property.",
  },
  Registrant: {
    title: "Registrant",
    body: "The organization or person that holds this registration.",
  },
  "Additional Information": {
    title: "Additional Information",
    body: "Other registration questions that are not party contacts.",
  },
  "De-registration answers": {
    title: "De-registration answers",
    body: "Answers given when this registration was de-registered.",
  },
  Payments: {
    title: "Payments",
    body: "Payments and fee adjustments recorded for this registration.",
  },
  "Registration period compliance": {
    title: "Registration period compliance",
    body: "Whether each registration period is paid and current.",
  },
  History: {
    title: "History",
    body: "Who created this registration and who last changed it.",
  },
  Inspections: {
    title: "Inspections",
    body: "Inspections recorded for this property.",
  },
} as const;

export type DetailsTileName = keyof typeof DETAILS_TILE_INFO;

export function DetailsTileTitle({
  name,
  children,
  infoBody,
}: {
  name: DetailsTileName;
  children?: ReactNode;
  /** Replaces the default explanation for this tile. */
  infoBody?: string;
}) {
  const copy = DETAILS_TILE_INFO[name];
  return (
    <CardTitle className="flex items-center gap-1 font-semibold text-xl">
      <span>{children ?? name}</span>
      <TileInfo
        label={copy.title}
        title={copy.title}
        body={infoBody ?? copy.body}
      />
    </CardTitle>
  );
}
